<script setup>
import { metamaPackage } from "~/data/compareData";
import { metamaPlans } from "~/data/plans";
import Flash from "~/components/Icon/Flash.vue";
import Infinity from "~/components/Icon/Infinity.vue";
import Setting from "~/components/Icon/Setting.vue";
import Wallet from "~/components/Icon/Wallet.vue";
import { createProductSchema } from "~/schemas/Product";

const { data: page } = await useAsyncData(() => 
  queryCollection('MetamaPage').first()
);

const appVersions = useAppVersions();

useSeoMeta({
  title: page.value?.metaTitle,
  description: page.value?.metaDescription,
  ogUrl: page.value?.linkCanonical,
  ogTitle: page.value?.title,
  ogDescription: page.value?.description,
});

useHead({
  link: [
    { rel: "canonical", href: page.value?.linkCanonical },
  ],
  script: [
    {
      type: 'application/ld+json',
      textContent: JSON.stringify(createProductSchema({
        name: page.value?.name ?? '',
        img: page.value?.img ?? '',
        description: page.value?.description ?? '',
        url: page.value?.linkCanonical ?? '',
        lowPrice: page.value?.lowPrice ?? '',
        highPrice: page.value?.highPrice ?? '',
        count: page.value?.count ?? '',
      }))
    },
  ],
});

const heroData = {
  title: page.value?.title,
  desc: page.value?.description,
  img: page.value?.img,
};

const isModalShow = ref(false);

const appData = computed(() => ({
  title: "دانلود اپلیکیشن ",
  items: appVersions.data.value.metama,
}));

const moreInfoData = {
  id: "1",
  title: "",
  items: [
    {
      id: "1",
      text: "تلفن اینترنتی چیست؟ | راهنمای خرید تلفن اینترنتی",
      img: "/img/articles/matama3.webp",
      url: `https://navatel.ir/blog/what-is-internet-phone/`,
    },
    {
      id: "2",
      text: "تفاوت مرکز تلفن ابری و ویپ محلی | بررسی کاربردها، مزایا و معایب",
      img: "/img/articles/cloudPbxVsVoip.webp",
      url: `https://navatel.ir/blog/hosted-pbx-vs-on-premise-pbx/`,
    },
    {
      id: "3",
      text: "متاما چیست؟| 0 تا 100 مراحل راه اندازی",
      img: "/img/articles/matama1.webp",
      url: `https://navatel.ir/blog/what-is-metama/`,
    },
  ],
};

const metamaProperties = [
  {
    icon: Flash,
    title: "راه‌اندازی سریع و بی‌دردسر",
    desc: "مرکز تلفن ابری خود را در کوتاه‌ترین زمان و تنها با چند کلیک فعال کنید.",
  },
  {
    icon: Infinity,
    title: "دسترسی بدون محدودیت",
    desc: "با موبایل یا دسکتاپ، مدیریت تماس‌ها از هر مکانی در دستان شماست.",
  },
  {
    icon: Setting,
    title: "مقیاس‌پذیری",
    desc: "تعداد کاربران و امکانات مرکز تلفن ابری را براساس نیاز کسب‌وکارتان تنظیم کنید.",
  },
  {
    icon: Wallet,
    title: "کاهش هزینه‌های ارتباطی",
    desc: "با هزینه‌ای کمتر از تلفن سنتی امکانات پیشرفته‌تری در اختیار داشته باشید.",
  },
];
</script>

<template>
  <div>
    <div class="bg-linear-to-b from-[#0043A9] to-[#0085DB1F] relative pt-2 lg:pt-16 pb-48">
      <MetamaHero :details="heroData" />
      <NuxtImg 
        width="500"
        src="/img/bgCloud.webp" 
        class="w-full h-[600px] object-cover absolute left-0 bottom-0 z-0"
        alt="متاما"
      />
      <div class="w-full absolute top-[800px] md:top-[700px] lg:top-auto lg:-bottom-10 left-0">
        <div 
          class="bg-white gridClass rounded-2xl shadow-[0_12px_42px_-4px_#0085DB1F] 
            grid grid-cols-1 lg:grid-cols-4 justify-center p-3"
        >
          <div
            v-for="item in metamaProperties"
            :key="item.title"
            class="flex flex-col items-start gap-2 p-6"
          >
            <div class="w-9 h-9 bg-gray-100 rounded-full p-2">
              <component :is="item.icon" class="w-full text-gray-400" />
            </div>
            <h4 class="text-gray-500 font-semibold">{{ item.title }}</h4>
            <p class="text-gray-400 text-sm text-start">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="gridClass mt-140 lg:mt-0">
      <SectionsFeatures 
        :title="page?.features.title"
        :desc="page?.features.desc"
        :features="page?.features.items"
      />
    </div>
    <div class="gridClass">
      <LazySectionsBenefits
        :title="page?.benefits.title"
        :desc="page?.benefits.desc"
        :benefits="page?.benefits.items"
        hydrate-on-visible
      />
    </div>
    <div class="bg-gray-50 pb-10 pt-20">
      <section class="gridClass" id="metama_price">
        <h2>
          بسته های مرکز تماس ابری متاما
        </h2>
        <div class="mb-10 tablet:flex justify-around gap-4">
          <div
            class="w-full tablet:w-[30%] lg:w-[25%] mt-12 p-4 custom-shadow bg-white relative rounded-xl"
            v-for="item in metamaPlans"
            :key="'f' + item.id"
          >
            <ProductPlan :item="item" />
          </div>
        </div>
        <div
          class="py-6 px-2 mt-8 bg-gray-200 flex flex-col lg:flex-row justify-center items-center"
        >
          <span class="me-6 font-bold text-black text-sm"
            >در مورد بسته ها نیاز به اطلاعات بیشتر دارید؟</span
          >
          <BaseButton
            @click="isModalShow = true"
            isLean
            size="2xl"
            text="مقایسه بسته ها"
            color="primary"
          />
        </div>
        <SectionModalCompare
          :isShow="isModalShow"
          @close="isModalShow = false"
          :data="metamaPackage"
        />
      </section>
    </div>
    <div class="bg-gray-50 py-10">
      <h2 class="gridClass">
        تعرفه‌های برقراری تماس متاما بر حسب تومان بر دقیقه
      </h2>
      <MetamaTableCompare title="" tci/>
    </div>
    <MetamaDownloadApp
      :details="appData"
      id="metama_app_url"
      class="mt-20"
    />
    <div class="gridClass">
      <SectionsFAQ
        :title="page?.FAQ.title"
        :items="page?.FAQ.items"
      />
    </div>
    <h2>درباره متاما بیشتر بدانید</h2>
    <ProductMoreInformation :details="moreInfoData" />
  </div>
</template>